import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, Storefront, ArrowLeft, Star, Users, CalendarDots, Medal, Crown, MapPin, Check, Lightning } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  legendary: { color: '#F59E0B', bg: '#F59E0B', label: 'Legendary' },
  elite: { color: '#9D4CDD', bg: '#9D4CDD', label: 'Elite' },
  established: { color: '#00F0FF', bg: '#00F0FF', label: 'Established' },
  rising: { color: '#10B981', bg: '#10B981', label: 'Rising' },
  newcomer: { color: '#71717A', bg: '#71717A', label: 'Newcomer' },
};

export default function ShopLeaderboardPage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/rbac/shop-leaderboard?limit=20`).then(r => {
      setLeaderboard(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const viewReputation = async (shopId) => {
    const { data } = await axios.get(`${API_URL}/api/rbac/shop-reputation/${shopId}`);
    setReputation(data);
    setSelectedShop(shopId);
  };

  return (
    <div className="app-container" data-testid="shop-leaderboard-page">
      <header className="p-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[#A1A1AA] hover:text-white"><ArrowLeft size={24} /></button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Trophy size={24} weight="duotone" className="text-[#F59E0B]" />Shop Leaderboard
            </h1>
            <p className="text-[#A1A1AA] text-sm">Top shops ranked by reputation</p>
          </div>
        </motion.div>
      </header>

      <div className="px-6 pb-32">
        {/* Reputation Detail Modal */}
        {reputation && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-5 rounded-2xl bg-[#0F0F16] border border-white/10" data-testid="reputation-detail">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-bold text-lg">{reputation.shop_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${TIER_CONFIG[reputation.tier]?.bg || '#666'}20`, color: TIER_CONFIG[reputation.tier]?.color || '#666' }}>
                    {TIER_CONFIG[reputation.tier]?.label || reputation.tier}
                  </span>
                  {reputation.verified && <Check size={14} className="text-[#00F0FF]" />}
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{reputation.total_score}</p>
                <p className="text-[10px] text-[#777]">/ {reputation.max_score} pts</p>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="space-y-2.5 mb-4">
              {[
                { key: 'events_hosted', label: 'Events Hosted', max: 50, icon: CalendarDots, color: '#00F0FF' },
                { key: 'attendance', label: 'Attendance', max: 50, icon: Users, color: '#10B981' },
                { key: 'tournaments', label: 'Tournaments', max: 30, icon: Trophy, color: '#F59E0B' },
                { key: 'badges_awarded', label: 'Badges Awarded', max: 20, icon: Medal, color: '#EC4899' },
                { key: 'members', label: 'Members', max: 25, icon: Users, color: '#3B82F6' },
              ].map(({ key, label, max, icon: Icon, color }) => (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#A1A1AA] flex items-center gap-1"><Icon size={12} style={{ color }} /> {label}</span>
                    <span className="text-white">{reputation.breakdown[key]}/{max}</span>
                  </div>
                  <div className="h-1.5 bg-[#1a1a2e] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(reputation.breakdown[key] / max) * 100}%`, backgroundColor: color }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Reputation Badges */}
            {reputation.reputation_badges?.length > 0 && (
              <div>
                <p className="text-xs text-[#777] mb-2">Reputation Badges</p>
                <div className="flex flex-wrap gap-2">
                  {reputation.reputation_badges.map(b => (
                    <span key={b.id} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B]">
                      <Star size={10} weight="fill" /> {b.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => { setReputation(null); setSelectedShop(null); }} className="text-xs text-[#555] mt-3 hover:text-white">Close details</button>
          </motion.div>
        )}

        {/* Leaderboard List */}
        {loading ? (
          <div className="space-y-3">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-20 rounded-2xl bg-[#0F0F16] animate-pulse" />)}</div>
        ) : leaderboard.length > 0 ? (
          <div className="space-y-2" data-testid="leaderboard-list">
            {leaderboard.map((entry, idx) => {
              const tier = TIER_CONFIG[entry.tier] || TIER_CONFIG.newcomer;
              const isTop3 = idx < 3;
              return (
                <motion.div key={entry.shop_id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
                  onClick={() => viewReputation(entry.shop_id)}
                  className={`p-4 rounded-2xl cursor-pointer transition-all border ${selectedShop === entry.shop_id ? 'border-[#F59E0B]/40 bg-[#F59E0B]/5' : 'border-white/5 bg-[#0F0F16] hover:border-white/10'}`}
                  data-testid={`leaderboard-entry-${idx}`}>
                  <div className="flex items-center gap-3">
                    {/* Rank */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${isTop3 ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#1a1a2e] text-[#777]'}`}>
                      {isTop3 ? <Crown size={18} weight="fill" className="text-[#F59E0B]" /> : `#${entry.rank}`}
                    </div>

                    {/* Shop Icon */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tier.bg}15` }}>
                      <Storefront size={20} weight="duotone" style={{ color: tier.color }} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-white font-medium text-sm truncate">{entry.shop_name}</p>
                        {entry.verified && <Check size={12} className="text-[#00F0FF] flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-[#777] mt-0.5">
                        {entry.location && <span className="flex items-center gap-0.5"><MapPin size={10} /> {entry.location}</span>}
                        <span className="flex items-center gap-0.5"><Users size={10} /> {entry.member_count || 0}</span>
                      </div>
                    </div>

                    {/* Score & Tier */}
                    <div className="text-right">
                      <p className="text-white font-bold">{entry.reputation_score}</p>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${tier.bg}15`, color: tier.color }}>{tier.label}</span>
                    </div>
                  </div>

                  {/* Rep Badges */}
                  {entry.reputation_badges?.length > 0 && (
                    <div className="flex gap-1 mt-2 ml-[52px]">
                      {entry.reputation_badges.slice(0, 3).map(b => (
                        <span key={b.id} className="text-[9px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B]">{b.name}</span>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-[#71717A]">
            <Trophy size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
            <p>No shops yet</p>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
